import { Color, Spacing } from '@persona-id/design-tokens';
import { FilterIcon } from '@persona-id/design-tokens/icons';
import { useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';

import { ExtractionType, MatchLevel } from 'dashboard/models/document';
import Button from 'design-system/components/Button';
import Checkbox from 'design-system/components/Checkbox';
import { CheckboxGroupField } from 'design-system/components/FormField';
import Tooltip from 'design-system/components/Tooltip';
import defaultTheme from 'design-system/theme';

interface DocumentExtractionFilterProps {
  extractionFilters: ExtractionFilterValue[];
  setExtractionFilters: (extractionFilters: ExtractionFilterValue[]) => void;
}

export enum ExtractionFilterValue {
  FullGuided = 'fullGuided',
  PartialGuided = 'partialGuided',
  Unguided = 'unguided',
}

export const ExtractionFilterOptions = [
  {
    label: 'Full match, guided extraction',
    value: ExtractionFilterValue.FullGuided,
  },
  {
    label: 'Partial, guided extraction',
    value: ExtractionFilterValue.PartialGuided,
  },
  {
    label: 'Unguided extraction',
    value: ExtractionFilterValue.Unguided,
  },
];

export const getMatchLevelAndExtractionTypeForFilter = (
  extractionFilterValue: ExtractionFilterValue,
) => {
  switch (extractionFilterValue) {
    case ExtractionFilterValue.FullGuided:
      return {
        matchLevel: MatchLevel.Full,
        extractionType: ExtractionType.Guided,
      };
    case ExtractionFilterValue.PartialGuided:
      return {
        matchLevel: MatchLevel.Partial,
        extractionType: ExtractionType.Guided,
      };
    case ExtractionFilterValue.Unguided:
      return {
        matchLevel: MatchLevel.Full,
        extractionType: ExtractionType.Unguided,
      };
  }
};

const StyledCheckboxGroupField = styled(CheckboxGroupField)`
  padding: ${Spacing[2]};
`;

const StyledCheckbox = styled(Checkbox)`
  padding: ${Spacing[1]};
`;

const HeaderFilter = styled('div')`
  display: flex;
  align-items: center;
`;

const HeaderStyledButton = styled(Button)<{ isShown?: boolean }>`
  background-color: ${({ isShown }) => (isShown ? Color.PrimaryWhite : Color.CoreSlate900)};
  margin: 0px ${Spacing[2]};
  svg path {
    ${({ isShown }) => isShown && `stroke: ${Color.CorePurple700};`}
  }
  // Override Button svg stroke style
  svg.pds-icon {
    path {
      ${({ isShown }) => isShown && `stroke: ${Color.CorePurple700};`}
    }

    path[stroke] {
      ${({ isShown }) => isShown && `stroke: ${Color.CorePurple700};`}
    }
  }
`;

const DocumentViewerExtractionFilter = ({
  extractionFilters,
  setExtractionFilters,
}: DocumentExtractionFilterProps) => {
  const [showFilterOptions, setShowFilterOptions] = useState(false);

  return (
    <ThemeProvider theme={defaultTheme}>
      <HeaderFilter>
        <Tooltip
          hideArrow
          side='bottom'
          open={showFilterOptions}
          interactive={false}
          trigger={
            <HeaderStyledButton
              data-test='document-viewer__header--filter-button'
              width='icon'
              variant='primary'
              isShown={showFilterOptions}
              id='document-viewer__header__extraction-filter'
              onClick={() => setShowFilterOptions(!showFilterOptions)}
            >
              <FilterIcon />
            </HeaderStyledButton>
          }
          onPointerDownOutside={() => setShowFilterOptions(false)}
          onEscapeKeyDown={() => setShowFilterOptions(false)}
        >
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          <StyledCheckboxGroupField onChange={setExtractionFilters} value={extractionFilters}>
            {ExtractionFilterOptions.map((filterOption) => (
              <StyledCheckbox
                key={`document-viewer__filter--${filterOption.label}`}
                label={filterOption.label}
                value={filterOption.value}
              />
            ))}
          </StyledCheckboxGroupField>
        </Tooltip>
      </HeaderFilter>
    </ThemeProvider>
  );
};

export default DocumentViewerExtractionFilter;
